import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  Box,
  Icon,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { CustomCrew } from "hooks/crews/useGetAllCrews";
import { useCallback } from "react";

const filterByCrewStyles = {
  borderRadius: "8px",
  width: { xs: 90, sm: 110, md: 130, lg: 150 },
  minWidth: 80,
  padding: "15px 20px 15px 0px",
  fontSize: "0.75rem",
  backgroundColor: "white!important",
  "& .MuiSelect-select": {
    padding: 0,
    fontSize: "0.75rem",
    paddingRight: "32px !important",
  },
  "& .MuiMenuItem-root": {
    fontSize: "0.625rem",
    minHeight: "24px",
    padding: "2px 8px",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  "& .MuiListItemText-primary": {
    fontSize: "0.625rem",
    margin: 0,
  },
  "& .MuiCheckbox-root": {
    padding: "2px",
    marginRight: "4px",
  },
  "& .MuiPaper-root": {
    maxHeight: 300,
  },
};

const filterByProjectManagerStyles = {
  ...filterByCrewStyles,
};

const menuItemStyles = {
  fontSize: "0.625rem",
  minHeight: "24px",
  padding: "2px 8px",
};

interface ScheduleFiltersProps {
  checked: Record<string, (CustomCrew & { checked: boolean }) | undefined>;
  crewOptions: Array<{ label: string; value: string }>;
  projectManagerOptions: Array<{ label: string; value: string }>;
  selectedProjectManagers: string[];
  onCrewChange: (selectedValues: string[]) => void;
  onProjectManagerChange: (selectedValues: string[]) => void;
}

export default function ScheduleFilters({
  checked,
  crewOptions,
  projectManagerOptions,
  selectedProjectManagers,
  onCrewChange,
  onProjectManagerChange,
}: ScheduleFiltersProps) {
  const MenuProps = {
    PaperProps: {
      sx: {
        maxHeight: 600,
        "& .MuiList-root": {
          padding: 0,
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
        },
        "& .MuiDivider-root": {
          margin: "0 !important",
        },
      },
    },
  };

  const renderSelectAllOptions = (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        bgcolor: "background.paper",
        borderTop: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <MenuItem
        onClick={() => onCrewChange(crewOptions.map((crew) => crew.value))}
        sx={{ ...menuItemStyles, flex: 1, justifyContent: "center" }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon fontSize="small" color="info">
            done_all
          </Icon>
          <MDTypography variant="button" color="info">
            Select All
          </MDTypography>
        </MDBox>
      </MenuItem>
      <Box sx={{ width: "1px", bgcolor: "rgba(0, 0, 0, 0.12)" }} />
      <MenuItem
        onClick={() => onCrewChange([])}
        sx={{ ...menuItemStyles, flex: 1, justifyContent: "center" }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon fontSize="small" color="info">
            clear_all
          </Icon>
          <MDTypography variant="button" color="info">
            Deselect All
          </MDTypography>
        </MDBox>
      </MenuItem>
    </Box>
  );

  const renderProjectManagerSelectAllOptions = (
    <Box
      sx={{
        borderRadius: "8px",
        position: "sticky",
        bottom: 0,
        bgcolor: "background.paper",
        borderTop: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <MenuItem
        onClick={() => onProjectManagerChange(projectManagerOptions.map((pm) => pm.value))}
        sx={{ ...menuItemStyles, flex: 1, justifyContent: "center" }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon fontSize="small" color="info">
            done_all
          </Icon>
          <MDTypography variant="button" color="info">
            Select All
          </MDTypography>
        </MDBox>
      </MenuItem>
      <Box sx={{ width: "1px", bgcolor: "rgba(0, 0, 0, 0.12)" }} />
      <MenuItem
        onClick={() => onProjectManagerChange([])}
        sx={{ ...menuItemStyles, flex: 1, justifyContent: "center" }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon fontSize="small" color="info">
            clear_all
          </Icon>
          <MDTypography variant="button" color="info">
            Deselect All
          </MDTypography>
        </MDBox>
      </MenuItem>
    </Box>
  );

  const handleCrewToggle = useCallback(
    (crewId: string, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const currentSelections = Object.entries(checked)
        .filter(([_, value]) => value.checked)
        .map(([key]) => key);

      const newSelections = currentSelections.includes(crewId)
        ? currentSelections.filter((id) => id !== crewId)
        : [...currentSelections, crewId];

      onCrewChange(newSelections);
    },
    [checked, onCrewChange]
  );

  const handlePMToggle = useCallback(
    (pmId: string, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const newSelections = selectedProjectManagers.includes(pmId)
        ? selectedProjectManagers.filter((id) => id !== pmId)
        : [...selectedProjectManagers, pmId];

      onProjectManagerChange(newSelections);
    },
    [selectedProjectManagers, onProjectManagerChange]
  );

  return (
    <MDBox display="flex" gap={2} sx={{ backgroundColor: "#f7f9fa!important" }}>
      <FormControl sx={{ width: { xs: 90, sm: 110, md: 130, lg: 150 }, minWidth: 80 }}>
        <InputLabel id="crew-filter-label" sx={{ fontSize: "0.75rem" }}>
          Filter by crew
        </InputLabel>
        <Select
          labelId="crew-filter-label"
          label="Filter by crew"
          multiple
          MenuProps={MenuProps}
          value={Object.entries(checked)
            .filter(([_, value]) => value.checked)
            .map(([key]) => key)}
          onChange={(event: SelectChangeEvent<string[]>) => {
            onCrewChange(event.target.value as string[]);
          }}
          renderValue={(selected) =>
            selected
              .map((value) => crewOptions.find((crew) => crew.value === value)?.label)
              .join(", ")
          }
          sx={filterByCrewStyles}
        >
          <Box sx={{ maxHeight: 500, overflow: "auto" }}>
            {crewOptions.map((crewOption) => (
              <MenuItem
                key={crewOption.value}
                value={crewOption.value}
                onClick={(e) => handleCrewToggle(crewOption.value, e)}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "transparent !important",
                    "&:hover": {
                      backgroundColor: `#${checked[crewOption.value]?.color}10`,
                    },
                  },
                  "&:hover": {
                    backgroundColor: `#${checked[crewOption.value]?.color}10`,
                  },
                  cursor: "pointer",
                }}
              >
                <Checkbox
                  size="small"
                  checked={checked[crewOption.value]?.checked ?? false}
                  sx={{
                    color: `#${checked[crewOption.value]?.color}`,
                    "&.Mui-checked": {
                      color: `#${checked[crewOption.value]?.color}`,
                    },
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <ListItemText
                  primaryTypographyProps={{ fontSize: "14px" }}
                  primary={crewOption.label}
                />
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    ml: "auto",
                    backgroundColor: `#${checked[crewOption.value]?.color}`,
                    borderRadius: "2px",
                  }}
                />
              </MenuItem>
            ))}
          </Box>
          {renderSelectAllOptions}
        </Select>
      </FormControl>

      <FormControl sx={{ width: { xs: 90, sm: 110, md: 130, lg: 150 }, minWidth: 80 }}>
        <InputLabel id="project-manager-filter-label" sx={{ fontSize: "0.75rem" }}>
          Filter by Project Manager
        </InputLabel>
        <Select
          labelId="project-manager-filter-label"
          label="Filter by Project Manager"
          multiple
          MenuProps={MenuProps}
          value={selectedProjectManagers}
          onChange={(event: SelectChangeEvent<string[]>) => {
            onProjectManagerChange(event.target.value as string[]);
          }}
          renderValue={(selected) =>
            selected
              .map((value) => projectManagerOptions.find((pm) => pm.value === value)?.label)
              .join(", ")
          }
          sx={filterByProjectManagerStyles}
        >
          <Box sx={{ maxHeight: 500, overflow: "auto" }}>
            {projectManagerOptions.map((pmOption) => (
              <MenuItem
                key={pmOption.value}
                value={pmOption.value}
                onClick={(e) => handlePMToggle(pmOption.value, e)}
                sx={{ cursor: "pointer" }}
              >
                <Checkbox
                  size="small"
                  checked={selectedProjectManagers.includes(pmOption.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                <ListItemText
                  primaryTypographyProps={{ fontSize: "14px" }}
                  primary={pmOption.label}
                />
              </MenuItem>
            ))}
          </Box>
          {renderProjectManagerSelectAllOptions}
        </Select>
      </FormControl>
    </MDBox>
  );
}
